import { useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";
import { useMe } from "../App";
import { api, useApi } from "../lib/api";
import { bytes, fullDate, parentOf, when } from "../lib/format";
import { FileIcon } from "../components/icons";
import { Button, cx, Empty, ErrorNote, Spinner, toast } from "../components/ui";

interface Item {
  path: string;
  name: string;
  markerVersionId: string;
  deletedAt: string;
  size: number;
}

const DAY = 86_400_000;

export default function Trash() {
  const { refreshMe } = useMe();
  const trash = useApi<{ items: Item[] }>("/trash");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const items = trash.data?.items ?? [];
  const key = (i: Item) => `${i.path}\n${i.markerVersionId}`;

  const restore = async (list: Item[]) => {
    setBusy(true);
    try {
      await api("/trash/restore", { body: { items: list.map((i) => ({ path: i.path, markerVersionId: i.markerVersionId })) } });
      toast(list.length === 1 ? `Restored ${list[0].name}` : `Restored ${list.length} files`);
      setSelected(new Set());
      await Promise.all([trash.reload(), refreshMe()]);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
      <h1 className="text-lg font-semibold">Recently deleted</h1>
      <p className="text-sm text-muted mt-1">Deleted files are kept for 30 days, then removed for good. Restoring puts a file back where it was.</p>
      <div className="flex items-center gap-2 mt-4 min-h-10">
        {selected.size > 0 && (
          <Button variant="primary" busy={busy} onClick={() => restore(items.filter((i) => selected.has(key(i))))}>
            <RotateCcw className="size-4" /> Restore {selected.size}
          </Button>
        )}
      </div>
      <div className="mt-2">
        {trash.error ? (
          <ErrorNote>{trash.error}</ErrorNote>
        ) : !trash.data ? (
          <Spinner />
        ) : !items.length ? (
          <div className="border border-dashed border-line rounded-xl">
            <Empty icon={<Trash2 className="size-8" />} title="Nothing deleted recently" />
          </div>
        ) : (
          <ul className="border border-line rounded-xl bg-surface divide-y divide-line overflow-hidden">
            {items.map((i) => {
              const daysLeft = Math.max(0, 30 - Math.floor((Date.now() - new Date(i.deletedAt).getTime()) / DAY));
              return (
                <li key={key(i)} className={cx("flex items-center gap-3 px-4 py-3", selected.has(key(i)) && "bg-accent-soft")}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${i.name}`}
                    checked={selected.has(key(i))}
                    onChange={() => {
                      const n = new Set(selected);
                      if (n.has(key(i))) n.delete(key(i));
                      else n.add(key(i));
                      setSelected(n);
                    }}
                  />
                  <FileIcon name={i.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{i.name}</div>
                    <div className="text-xs text-muted truncate" title={fullDate(i.deletedAt)}>
                      {parentOf(i.path) ? `In ${parentOf(i.path).slice(0, -1)} · ` : ""}
                      Deleted {when(i.deletedAt)} · {bytes(i.size)} · {daysLeft} day{daysLeft === 1 ? "" : "s"} left
                    </div>
                  </div>
                  <Button variant="ghost" disabled={busy} onClick={() => restore([i])}>
                    <RotateCcw className="size-4" /> <span className="hidden sm:inline">Restore</span>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
